import { memo } from "react";

/**
 * FacilityOverviewCard: 施設概要カード（1 件、aspect 2:1 画像 + テキスト + CTA）
 *
 * ゲスト側 app/frontend/guest/plain/src/components/top/FacilityOverviewCard.astro の逐語再現:
 * - Mobile: 縦積み（flex-col）、画像 aspect 2:1、テキスト p-4、CTA は w-full
 * - Tablet+: 横並び（flex-row）、画像 w-2/5 h-[200px]、テキスト p-6、CTA は w-fit
 *
 * 値を変更する場合は必ずゲスト側も確認・同期すること。
 */

type Props = {
	hotelName: string;
	catchCopy?: string;
	mainKeyVisualUrl: string;
	primaryColor: string;
	ctaLabel?: string;
};

export const FacilityOverviewCard = memo(
	({
		hotelName,
		catchCopy,
		mainKeyVisualUrl,
		primaryColor,
		ctaLabel = "施設について",
	}: Props) => {
		return (
			<section className="overflow-hidden rounded-lg bg-white">
				<div className="flex flex-col tablet:flex-row">
					{/*
					 * 画像エリア:
					 * - mobile: padding-bottom 50% で 2:1 アスペクト比を確保
					 * - tablet 以上: pb-0 + 固定幅 40% + min-height 200px。高さは flex-row の
					 *   align-items:stretch デフォルトで text 側と一致するため、テキストが長くても
					 *   画像下に余白ができず、カード全体 height いっぱいに画像が表示される。
					 *
					 * 旧実装の tablet:h-[200px] 固定は text 側が 200px より大きい場合に画像下に
					 * 白空白を残す原因だったため、min-h + 暗黙的 stretch に置き換え。
					 */}
					<div className="relative w-full overflow-hidden pb-[50%] tablet:min-h-[200px] tablet:w-2/5 tablet:shrink-0 tablet:pb-0">
						{mainKeyVisualUrl ? (
							<img
								src={mainKeyVisualUrl}
								alt={hotelName}
								loading="lazy"
								className="absolute inset-0 h-full w-full object-cover"
							/>
						) : (
							<div className="absolute inset-0 h-full w-full bg-zinc-100" />
						)}
					</div>

					{/* テキストエリア */}
					<div
						className="flex flex-1 flex-col justify-center gap-3 p-4 tablet:p-6"
					>
						<h2 className="text-xl font-bold text-zinc-900">{hotelName}</h2>
						{catchCopy && <p className="text-sm leading-relaxed text-zinc-600">{catchCopy}</p>}
						<div
							className="mt-2 inline-flex h-9 w-full items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-white tablet:w-fit"
							style={{ backgroundColor: primaryColor }}
						>
							{ctaLabel}
						</div>
					</div>
				</div>
			</section>
		);
	},
);

FacilityOverviewCard.displayName = "FacilityOverviewCard";
